import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { FaqItem } from '@/contexts/SiteDataContext';

export default function FaqSection() {
  const { data, updateFaqs } = useSiteData();
  const [faqs, setFaqs] = useState<FaqItem[]>([...data.faqs]);
  const [saved, setSaved] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const handleSave = () => { updateFaqs(faqs); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  const update = (i: number, field: keyof FaqItem, val: string) => setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [field]: val } : f));
  const add = () => { setFaqs([...faqs, { question: 'New Question?', answer: 'Answer here...' }]); setExpandedIdx(faqs.length); };
  const remove = (i: number) => setFaqs(faqs.filter((_, idx) => idx !== i));
  const moveUp = (i: number) => { if (i === 0) return; const f = [...faqs]; [f[i - 1], f[i]] = [f[i], f[i - 1]]; setFaqs(f); };
  const moveDown = (i: number) => { if (i === faqs.length - 1) return; const f = [...faqs]; [f[i], f[i + 1]] = [f[i + 1], f[i]]; setFaqs(f); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">FAQ</h2>
          <p className="text-foreground-500 text-sm">Manage the Frequently Asked Questions on the Contact page. ({faqs.length} questions)</p>
        </div>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap">
          <i className="ri-add-line"></i> Add Question
        </button>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, i) => (
          <div key={i} className="border border-background-200 rounded-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-background-50 cursor-pointer" onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
              <div className="flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); moveUp(i); }} disabled={i === 0} className="w-6 h-6 flex items-center justify-center rounded bg-background-200 hover:bg-background-300 disabled:opacity-30 transition-colors cursor-pointer">
                  <i className="ri-arrow-up-line text-[10px] text-foreground-600"></i>
                </button>
                <button onClick={(e) => { e.stopPropagation(); moveDown(i); }} disabled={i === faqs.length - 1} className="w-6 h-6 flex items-center justify-center rounded bg-background-200 hover:bg-background-300 disabled:opacity-30 transition-colors cursor-pointer">
                  <i className="ri-arrow-down-line text-[10px] text-foreground-600"></i>
                </button>
              </div>
              <span className="flex-1 text-sm font-semibold text-foreground-800 truncate">{faq.question}</span>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); remove(i); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
                <i className={`${expandedIdx === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
              </div>
            </div>
            {expandedIdx === i && (
              <div className="p-4 space-y-3 border-t border-background-200 bg-white">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => update(i, 'question', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Answer</label>
                  <textarea rows={4} value={faq.answer} onChange={(e) => update(i, 'answer', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={handleSave} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save FAQs'}
      </button>
    </div>
  );
}